import type {VoidFunctionComponent} from "react";
import React, {useState} from "react";
import s from "./DebugDeviceMockAndLogging.module.scss";
import {useDebugDeviceMockLogic} from "./DebugDeviceMock.logic";
import Button from "../../../components/common/Button";
import {WhenEnvIsDevLocal} from "../../../components/conditional/env-is/WhenEnvIsDevLocal";
import {logger} from "../../../services/log/logger";
import {loggerNamespaces, LogNamespaceKeyType, LogNamespaceType} from "../../../config/logger";
import Container from "../../../components/common/tools/Container/Container";
import Checkbox from "../../../components/common/tools/Checkbox/Checkbox";
import {useAppSelector} from "../../../redux/hooks";
import {createGuid} from "../../../util/string-util";

export interface IDebugDeviceMockProps {
}

export const DebugDeviceMockAndLogging: VoidFunctionComponent<IDebugDeviceMockProps> = ({}) => {
  const initialNamespacesForWhichLoggingIsEnabled = useAppSelector(logger.getEnabledNamespaces);
  const [namespacesForWhichLoggingIsEnabled, setNamespacesForWhichLoggingIsEnabled] = useState<string>(initialNamespacesForWhichLoggingIsEnabled);

  const {
    handleClickMockIPhone,
    handleClickMockIPad,
    handleClickMockWindowsDesktop,
    handleClickMockMacDesktop,
    handleClickMockAndroid,
  } = useDebugDeviceMockLogic();

  return (
    <WhenEnvIsDevLocal> {/** extra check to ensure only in dev local */}
      <div className={s.container}>
        <h3 className={s.title}>Debug - Device Mock</h3>
        <div className={s.mockButtons}>
          <Button text={"iPhone"} onClick={handleClickMockIPhone}/>
          <Button text={"iPad"} onClick={handleClickMockIPad}/>
          <Button text={"Windows"} onClick={handleClickMockWindowsDesktop}/>
          <Button text={"Mac"} onClick={handleClickMockMacDesktop}/>
          <Button text={"Android"} onClick={handleClickMockAndroid}/>
        </div>

        <h3 className={s.title}>Logging
          <a
            href={`https://playgroundsessions.atlassian.net/wiki/spaces/DEV/pages/2223734785/Logging`}
            target={`_blank`}
            style={{fontSize: "14px", cursor: "pointer", paddingLeft: "4px"}}>
            (documentation)
          </a>
        </h3>

        <div className={s.mockButtons}>
          <Button text={"All"}
            onClick={() => {
              logger.enableLoggingForAllNamespaces();
              setNamespacesForWhichLoggingIsEnabled(logger.getEnabledNamespaces());
            }}
          />
          <Button text={"Clear"}
            onClick={() => {
              logger.disabledLoggingForAllNamespaces();
              setNamespacesForWhichLoggingIsEnabled("");
            }}
          />
          <Container label={"Namespaces"} additionalClassName={s.namespaceContainer}>
            {getCheckboxesForNamespaces(loggerNamespaces, () => {
              setNamespacesForWhichLoggingIsEnabled(logger.getEnabledNamespaces);
            })}
          </Container>
          <Container label={"Call stack setting"} additionalClassName={s.callStackSetting}>
            <Button
              text={"Deferred"}
              onClick={() => { logger.setCallStackGlobalValue("deferred"); }}
              minWidth={150}
            />
            <br/>
            <Button
              text={"Show"}
              onClick={() => { logger.setCallStackGlobalValue("true"); }}
              minWidth={150}
            />
            <br/>
            <Button
              text={"Hide"}
              onClick={() => { logger.setCallStackGlobalValue("false"); }}
              minWidth={150}
            />
          </Container>

          <Container label={"Data setting"} additionalClassName={s.dataSetting}>
            <Button
              text={"Deferred"}
              onClick={() => { logger.setDataGlobalValue("deferred"); }}
              minWidth={250}
            />
            <br/>
            <Button
              text={"Show on one line"}
              onClick={() => { logger.setDataGlobalValue("true"); }}
              minWidth={250}
            />
            <br/>
            <Button
              text={"Show on multiple lines"}
              onClick={() => { logger.setDataGlobalValue("false"); }}
              minWidth={250}
            />
          </Container>
        </div>
      </div>
    </WhenEnvIsDevLocal>
  );
}

function getCheckboxesForNamespaces(
  loggerNamespaces: Record<LogNamespaceKeyType, LogNamespaceType>,
  callback: () => void,
) {
  const checkboxes = Object.entries(loggerNamespaces).map(([namespaceKey, namespace]) => {
    const isLoggingEnabledForNamespace = logger.isLoggingEnabledForNamespace(namespace);
    const id = createGuid();

    return (
      <Checkbox
        key={id}
        name={namespaceKey}
        isChecked={isLoggingEnabledForNamespace}
        label={namespaceKey}
        tooltip={`Enable logging for ${namespace}`}
        onChange={()=> {
          logger.toggleLoggingForNamespace(namespace);
          callback();
        }}
      />
    );
  });

  return checkboxes;
}
